import {
  ArrowRight,
  Building2,
  Briefcase,
  Store,
  Wifi,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Comparator helps a B2B visitor pick the right Intelbras IP PBX without
// having to read 4 tabs. Each card maps to a tab inside the Intelbras
// showcase via the `target` deep-link hash.

const COPY = {
  pt: {
    overline: "Qual central Intelbras escolher?",
    title: "Infinity, CIP 92200, CIP 850 ou IAD 100 — qual é a certa para si?",
    subtitle:
      "Comparativo rápido das 4 centrais telefónicas IP Intelbras representadas pela TVCabSat. Clique numa central para abrir a ficha completa com especificações, vídeo e produtos.",
    labels: {
      best_for: "Ideal para",
      capacity: "Capacidade",
      features: "Destaques",
      form: "Formato",
      cta: "Ver detalhes",
      pick: "Escolha mais comum",
    },
    cta_help: {
      title: "Ainda em dúvida?",
      body: "A nossa equipa avalia o cenário (ramais, sites, integrações) e recomenda a central certa em 24h.",
      btn: "Falar com um especialista",
    },
  },
  en: {
    overline: "Which Intelbras PBX should you pick?",
    title: "Infinity, CIP 92200, CIP 850 or IAD 100 — which one is right for you?",
    subtitle:
      "A quick side-by-side of the 4 Intelbras IP PBX systems we represent. Click any model to open the full detail tab with specs, video and product cards.",
    labels: {
      best_for: "Best for",
      capacity: "Capacity",
      features: "Highlights",
      form: "Form factor",
      cta: "See details",
      pick: "Most common choice",
    },
    cta_help: {
      title: "Still not sure?",
      body: "Our team assesses your scenario (extensions, sites, integrations) and recommends the right central within 24h.",
      btn: "Talk to a specialist",
    },
  },
  es: {
    overline: "¿Qué central Intelbras elegir?",
    title: "Infinity, CIP 92200, CIP 850 o IAD 100 — ¿cuál es la adecuada para ti?",
    subtitle:
      "Comparativa rápida de las 4 centrales telefónicas IP Intelbras representadas por TVCabSat. Haz clic en una central para abrir la ficha completa con especificaciones, vídeo y productos.",
    labels: {
      best_for: "Ideal para",
      capacity: "Capacidad",
      features: "Destacados",
      form: "Formato",
      cta: "Ver detalles",
      pick: "Opción más común",
    },
    cta_help: {
      title: "¿Aún tienes dudas?",
      body: "Nuestro equipo evalúa tu escenario (extensiones, sitios, integraciones) y recomienda la central adecuada en 24h.",
      btn: "Hablar con un especialista",
    },
  },
};

const CENTRALS = {
  pt: [
    {
      id: "infinity-solution",
      icon: "building",
      name: "Infinity Solution",
      subtitle: "Softswitch corporativo",
      target: "intelbras:infinity-solution",
      featured: false,
      best_for:
        "Grandes empresas, contact centers, operações multi-site e migração total para IP.",
      capacity: [
        "500 ramais IP",
        "640 ramais TDM",
        "120 canais E1",
        "Múltiplos sites",
      ],
      features: [
        "Arquitetura softswitch modular",
        "Gateways analógicos/digitais integrados",
        "Conferência de voz e DISA",
        "Roteamento inteligente",
      ],
      form: "Softswitch + gateways",
    },
    {
      id: "cip-92200",
      icon: "briefcase",
      name: "CIP 92200",
      subtitle: "Central IP all-in-one",
      target: "intelbras:cip-92200",
      featured: true,
      best_for:
        "Médias e grandes empresas, hotelaria, indústria — flexibilidade num único equipamento.",
      capacity: [
        "400 ramais IP",
        "60 canais E1 (R2 / ISDN)",
        "32 interfaces FXO/FXS",
        "Troncos SIP",
      ],
      features: [
        "Gateways integrados",
        "Rack 19″, programador web bilíngue",
        "Fax T.30/T.38",
        "DAC, DISA e correio de voz",
      ],
      form: "Rack 19″",
    },
    {
      id: "cip-850",
      icon: "store",
      name: "CIP 850",
      subtitle: "IP Gateway 1U",
      target: "intelbras:cip-850",
      featured: false,
      best_for:
        "Micro, pequenas e médias empresas, coworkings e provedores — versatilidade com mobilidade IP.",
      capacity: [
        "50 ramais IP",
        "16 chamadas simultâneas",
        "8 interfaces FXO/FXS (modulares)",
        "Troncos SIP",
      ],
      features: [
        "Softphone (mobile/PC) integrado",
        "Consulta de portabilidade numérica",
        "Vídeo-chamadas SIP",
        "DDNS — sem IP fixo",
      ],
      form: "Rack 19″ — 1U",
    },
    {
      id: "iad-100",
      icon: "wifi",
      name: "IAD 100",
      subtitle: "Central compacta + Wi-Fi + 4G",
      target: "intelbras:iad-100",
      featured: false,
      best_for:
        "Pequenos escritórios, retalho, clínicas, filiais — comunicação + internet num só dispositivo.",
      capacity: [
        "32 ramais IP + 32 troncos SIP",
        "9 chamadas simultâneas",
        "1 FXO + 1 FXS",
        "1 GSM/3G/4G LTE",
      ],
      features: [
        "Roteador Wi-Fi integrado",
        "VPN + Firewall + redundância",
        "4G LTE como backup de internet",
        "Substitui vários equipamentos",
      ],
      form: "Compacto — desktop",
    },
  ],
  en: [
    {
      id: "infinity-solution",
      icon: "building",
      name: "Infinity Solution",
      subtitle: "Corporate softswitch",
      target: "intelbras:infinity-solution",
      featured: false,
      best_for:
        "Large companies, contact centers, multi-site operations and full IP migration.",
      capacity: [
        "500 IP extensions",
        "640 TDM extensions",
        "120 E1 channels",
        "Multi-site support",
      ],
      features: [
        "Modular softswitch architecture",
        "Built-in analog/digital gateways",
        "Voice conferencing and DISA",
        "Intelligent call routing",
      ],
      form: "Softswitch + gateways",
    },
    {
      id: "cip-92200",
      icon: "briefcase",
      name: "CIP 92200",
      subtitle: "All-in-one IP PBX",
      target: "intelbras:cip-92200",
      featured: true,
      best_for:
        "Mid-sized and large companies, hospitality and industry — flexibility in a single device.",
      capacity: [
        "400 IP users",
        "60 E1 channels (R2 / ISDN)",
        "32 FXO/FXS interfaces",
        "SIP trunks",
      ],
      features: [
        "Integrated gateways",
        "19″ rack, bilingual web programmer",
        "T.30/T.38 fax",
        "ACD, DISA and voicemail",
      ],
      form: "19″ rack",
    },
    {
      id: "cip-850",
      icon: "store",
      name: "CIP 850",
      subtitle: "1U IP Gateway",
      target: "intelbras:cip-850",
      featured: false,
      best_for:
        "Micro, small and mid-sized businesses, coworking spaces and ISPs — versatile IP with mobility.",
      capacity: [
        "50 IP extensions",
        "16 simultaneous calls",
        "8 modular FXO/FXS interfaces",
        "SIP trunks",
      ],
      features: [
        "Built-in mobile / PC softphone",
        "Number portability lookup",
        "SIP video calls",
        "DDNS — no fixed IP required",
      ],
      form: "19″ rack — 1U",
    },
    {
      id: "iad-100",
      icon: "wifi",
      name: "IAD 100",
      subtitle: "Compact PBX + Wi-Fi + 4G",
      target: "intelbras:iad-100",
      featured: false,
      best_for:
        "Small offices, retail, clinics, branches — comms + internet in a single device.",
      capacity: [
        "32 IP extensions + 32 SIP trunks",
        "9 simultaneous calls",
        "1 FXO + 1 FXS",
        "1 GSM/3G/4G LTE",
      ],
      features: [
        "Integrated Wi-Fi router",
        "VPN + Firewall + data redundancy",
        "4G LTE as internet backup",
        "Replaces several devices",
      ],
      form: "Compact desktop",
    },
  ],
  es: [
    {
      id: "infinity-solution",
      icon: "building",
      name: "Infinity Solution",
      subtitle: "Softswitch corporativo",
      target: "intelbras:infinity-solution",
      featured: false,
      best_for:
        "Grandes empresas, contact centers, operaciones multi-sitio y migración total a IP.",
      capacity: [
        "500 extensiones IP",
        "640 extensiones TDM",
        "120 canales E1",
        "Multi-sitio",
      ],
      features: [
        "Arquitectura softswitch modular",
        "Gateways analógicos/digitales integrados",
        "Conferencia de voz y DISA",
        "Enrutamiento inteligente",
      ],
      form: "Softswitch + gateways",
    },
    {
      id: "cip-92200",
      icon: "briefcase",
      name: "CIP 92200",
      subtitle: "Central IP all-in-one",
      target: "intelbras:cip-92200",
      featured: true,
      best_for:
        "Medianas y grandes empresas, hotelería e industria — flexibilidad en un único equipo.",
      capacity: [
        "400 usuarios IP",
        "60 canales E1 (R2 / ISDN)",
        "32 interfaces FXO/FXS",
        "Troncales SIP",
      ],
      features: [
        "Gateways integrados",
        "Rack 19″, programador web bilingüe",
        "Fax T.30/T.38",
        "DAC, DISA y buzón de voz",
      ],
      form: "Rack 19″",
    },
    {
      id: "cip-850",
      icon: "store",
      name: "CIP 850",
      subtitle: "IP Gateway 1U",
      target: "intelbras:cip-850",
      featured: false,
      best_for:
        "Micro, pequeñas y medianas empresas, coworkings y proveedores — versatilidad IP con movilidad.",
      capacity: [
        "50 extensiones IP",
        "16 llamadas simultáneas",
        "8 interfaces FXO/FXS (modulares)",
        "Troncales SIP",
      ],
      features: [
        "Softphone (móvil / PC) integrado",
        "Consulta de portabilidad numérica",
        "Videollamadas SIP",
        "DDNS — sin IP fija",
      ],
      form: "Rack 19″ — 1U",
    },
    {
      id: "iad-100",
      icon: "wifi",
      name: "IAD 100",
      subtitle: "Central compacta + Wi-Fi + 4G",
      target: "intelbras:iad-100",
      featured: false,
      best_for:
        "Pequeñas oficinas, retail, clínicas, sucursales — comunicación + internet en un solo dispositivo.",
      capacity: [
        "32 extensiones IP + 32 troncales SIP",
        "9 llamadas simultáneas",
        "1 FXO + 1 FXS",
        "1 GSM/3G/4G LTE",
      ],
      features: [
        "Router Wi-Fi integrado",
        "VPN + Firewall + redundancia",
        "4G LTE como respaldo de internet",
        "Sustituye varios equipos",
      ],
      form: "Compacto — escritorio",
    },
  ],
};

const ICON_MAP = {
  building: Building2,
  briefcase: Briefcase,
  store: Store,
  wifi: Wifi,
};

const goToTarget = (target) => {
  const nextHash = `#${target}`;
  if (window.location.hash === nextHash) {
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  } else {
    window.location.hash = nextHash;
  }
};

export const IntelbrasComparator = () => {
  const { lang } = useLang();
  const copy = COPY[lang] || COPY.pt;
  const centrals = CENTRALS[lang] || CENTRALS.pt;

  return (
    <section
      id="intelbras-compare"
      data-testid="intelbras-comparator"
      className="relative scroll-mt-24 overflow-hidden border-t border-slate-200 bg-gradient-to-b from-[#e9f4ed] via-[#e3f0e7] to-[#d6e6f2] py-20 lg:py-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-1/4 h-[500px] w-[500px] rounded-full bg-emerald-200/30 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/4 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-emerald-700">
            {copy.overline}
          </div>
          <h2
            data-testid="intelbras-comparator-title"
            className="font-heading mt-4 text-3xl font-black tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
          >
            {copy.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">
            {copy.subtitle}
          </p>
        </div>

        {/* 4-card grid */}
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {centrals.map((c) => {
            const Icon = ICON_MAP[c.icon] || Building2;
            return (
              <article
                key={c.id}
                data-testid={`intelbras-compare-${c.id}`}
                className={`group relative flex flex-col rounded-3xl border bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl ${
                  c.featured
                    ? "border-emerald-300 ring-2 ring-emerald-200"
                    : "border-slate-200"
                }`}
              >
                {c.featured && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-md">
                    <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                    {copy.labels.pick}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div
                    className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                      c.featured
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-bold tracking-tight text-slate-900">
                      {c.name}
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                      {c.subtitle}
                    </div>
                  </div>
                </div>

                {/* Best for */}
                <div className="mt-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    {copy.labels.best_for}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                    {c.best_for}
                  </p>
                </div>

                {/* Capacity */}
                <div className="mt-4 rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-700">
                    {copy.labels.capacity}
                  </div>
                  <ul className="mt-2 space-y-1">
                    {c.capacity.map((cap) => (
                      <li
                        key={cap}
                        className="flex items-start gap-1.5 text-[12.5px] font-medium leading-snug text-slate-800"
                      >
                        <CheckCircle2
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600"
                          strokeWidth={2.5}
                        />
                        <span>{cap}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Highlights */}
                <div className="mt-4 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    {copy.labels.features}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {c.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-1.5 text-[12.5px] leading-snug text-slate-700"
                      >
                        <span
                          aria-hidden
                          className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Form factor + CTA */}
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <div className="text-[11px] font-semibold text-slate-500">
                    <span className="font-mono uppercase tracking-wider">
                      {copy.labels.form}:
                    </span>{" "}
                    <span className="text-slate-700">{c.form}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => goToTarget(c.target)}
                  data-testid={`intelbras-compare-cta-${c.id}`}
                  className={`mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                    c.featured
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-blue-700 hover:bg-blue-800"
                  }`}
                >
                  {copy.labels.cta}
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </article>
            );
          })}
        </div>

        {/* Help CTA */}
        <div className="mt-12 rounded-3xl border border-emerald-200 bg-gradient-to-br from-white via-emerald-50 to-white p-6 md:p-8">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                {copy.cta_help.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                {copy.cta_help.body}
              </p>
            </div>
            <a
              href="/#contact"
              data-testid="intelbras-comparator-help-cta"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-slate-800 hover:shadow-lg"
            >
              {copy.cta_help.btn}
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntelbrasComparator;
